import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, ChevronRight, ChevronLeft, Filter, X, Loader2, WifiOff } from "lucide-react";
import { Spinner, StatusBadge, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, fmtINR } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";
import { getPack } from "../../lib/offline";
import { useTerritoryMasters, TerritoryFilterSelects, matchesTerritory } from "../../components/TerritoryFilter";

const PAGE_SIZES = [25, 50, 100];
const PENDING = ["assigned", "reassigned", "visited"];
const DONE = ["collected", "partially_collected"];
const tabOf = (s) => (PENDING.includes(s) ? "pending" : DONE.includes(s) ? "done" : null);

/** Offline fallback: build the same {items,total,counts,...} page shape from the cached pack. */
function pageFromPack(props, { tab, search, territory, page, limit }) {
  let list = props.filter((p) => matchesTerritory(p, territory));
  if (search) {
    const s = search.toLowerCase();
    const hay = (p) => [p.pin, p.owner_name, p.address, p.house_no,
      ...(p.assessment_floors || []).map((f) => f.house_no)].filter(Boolean).join(" ").toLowerCase();
    list = list.filter((p) => hay(p).includes(s));
  }
  const counts = { all: list.length, pending: 0, done: 0 };
  list.forEach((p) => { const t = tabOf(p.assignment?.status); if (t) counts[t] += 1; });
  if (tab !== "all") list = list.filter((p) => tabOf(p.assignment?.status) === tab);
  const total = list.length;
  const pages = Math.max(1, Math.ceil(total / limit));
  const safePage = Math.min(page, pages);
  return {
    items: list.slice((safePage - 1) * limit, safePage * limit), total, page: safePage, limit, pages, counts,
    total_due: list.reduce((s, p) => s + (p.demand_summary?.balance || 0), 0), offline: true,
  };
}

export default function AssignedList() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const wardParam = searchParams.get("ward") || "";
  const [data, setData] = useState(null);           // {items,total,page,limit,pages,counts,total_due,offline?}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");            // debounced copy of `search` sent to the server
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [territory, setTerritory] = useState({ zoneId: "", wardId: wardParam, mohallaId: "" });
  const [showFilters, setShowFilters] = useState(!!wardParam);
  const territoryMasters = useTerritoryMasters(code);
  const listTop = useRef(null);
  const reqSeq = useRef(0);

  // debounce the search box so each keystroke does not hit the server
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);
  // any filter change starts again from page 1
  useEffect(() => { setPage(1); }, [query, tab, limit, territory.zoneId, territory.wardId, territory.mohallaId]);

  const load = useCallback(async () => {
    const reqId = ++reqSeq.current;          // ignore responses that arrive out of order
    setLoading(true);
    setError(false);
    const params = { tab, page, limit };
    if (query) params.search = query;
    if (territory.zoneId) params.zone_id = territory.zoneId;
    if (territory.wardId) params.ward_id = territory.wardId;
    if (territory.mohallaId) params.mohalla_id = territory.mohallaId;
    try {
      const r = await api.get(`/t/${code}/field/assigned`, { params });
      if (reqId !== reqSeq.current) return;
      setData(r.data);
    } catch (e) {
      if (reqId !== reqSeq.current) return;
      // offline fallback: page the cached pack locally
      const pack = await getPack(code).catch(() => null);
      if (pack?.assigned_properties) {
        setData(pageFromPack(pack.assigned_properties, { tab, search: query, territory, page, limit }));
      } else {
        setError(true);
      }
    } finally {
      if (reqId === reqSeq.current) setLoading(false);
    }
  }, [code, tab, page, limit, query, territory]);
  useEffect(() => { load(); }, [load]);
  useLiveCollections(code, () => { load(); });   // Phase 149: balances / collected status follow the live ledger

  const territoryActive = territory.zoneId || territory.wardId || territory.mohallaId;
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const counts = data?.counts || {};
  const from = total === 0 ? 0 : (data.page - 1) * data.limit + 1;
  const to = total === 0 ? 0 : Math.min(data.page * data.limit, total);
  const goTo = (p) => {
    setPage(p);
    listTop.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const withCount = (label, n) => (n == null ? label : `${label} (${n.toLocaleString("en-IN")})`);

  return (
    <div className="space-y-3" ref={listTop}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold font-heading">Assigned properties</h1>
          {data && (
            <p className="text-sm text-muted-foreground" data-testid="assigned-summary">
              {total.toLocaleString("en-IN")} propert{total === 1 ? "y" : "ies"} • {fmtINR(data.total_due)} due
              {data.offline && (
                <span className="inline-flex items-center gap-1 ml-2 text-amber-700" data-testid="assigned-offline-badge">
                  <WifiOff className="h-3 w-3" /> offline copy
                </span>
              )}
            </p>
          )}
        </div>
        <Button variant={showFilters || territoryActive ? "default" : "outline"} size="sm" className="h-9 shrink-0"
          onClick={() => setShowFilters((v) => !v)} data-testid="assigned-filter-button">
          <Filter className="h-4 w-4 mr-1.5" /> Filters{territoryActive ? " (1)" : ""}
        </Button>
      </div>
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9 h-11" placeholder="Search PIN, owner, house no, address..." value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="collector-search-input" />
        {loading && data && (
          <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin"
            data-testid="assigned-loading-indicator" />
        )}
      </div>

      {showFilters && (
        <Card className="shadow-sm" data-testid="assigned-filter-panel">
          <CardContent className="p-3 space-y-3">
            <TerritoryFilterSelects masters={territoryMasters} value={territory} onChange={setTerritory}
              testIdPrefix="assigned" />
            {territoryActive && (
              <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground"
                onClick={() => setTerritory({ zoneId: "", wardId: "", mohallaId: "" })}
                data-testid="assigned-clear-filters">
                <X className="h-3.5 w-3.5 mr-1" /> Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="all" data-testid="collector-tab-all">{withCount("All", counts.all)}</TabsTrigger>
          <TabsTrigger value="pending" data-testid="collector-tab-pending">{withCount("Pending", counts.pending)}</TabsTrigger>
          <TabsTrigger value="done" data-testid="collector-tab-done">{withCount("Collected", counts.done)}</TabsTrigger>
        </TabsList>
      </Tabs>

      {!data && loading ? <Spinner /> : error ? (
        <EmptyState title="Could not load assigned properties" subtitle="Check your connection and try again."
          action={<Button variant="outline" size="sm" onClick={load} data-testid="assigned-retry-button">Retry</Button>} />
      ) : items.length === 0 ? (
        <EmptyState title="No properties" subtitle={territoryActive
          ? "No assigned properties match the selected zone / ward / mohalla."
          : "No assigned properties match this filter"} />
      ) : (
        <>
          <div className={`space-y-3 ${loading ? "opacity-60" : ""}`} data-testid="collector-assigned-list">
            {items.map((p) => (
              <Link key={p.id} to={`/${code}/collector/property/${p.id}`} data-testid={`collector-property-card-${p.pin}`}>
                <Card className="shadow-sm active:scale-[0.99] transition-transform mb-3">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-mono text-sm font-semibold">{p.pin}</p>
                        <p className="font-medium text-sm truncate">{p.owner_name || <span className="text-muted-foreground">Owner not recorded</span>}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                          <MapPin className="h-3 w-3 shrink-0" /> {p.address || `${p.ward_label || ""} ${p.mohalla_label || ""}`}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 mt-1" />
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex gap-2">
                        <StatusBadge status={p.assignment?.status} />
                        <StatusBadge status={p.payment_status} />
                      </div>
                      <p className="font-semibold tabular-nums text-sm">{fmtINR(p.demand_summary?.balance || 0)} <span className="text-xs font-normal text-muted-foreground">due</span></p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* pagination */}
          <Card className="shadow-sm" data-testid="assigned-pagination">
            <CardContent className="p-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground tabular-nums" data-testid="assigned-page-range">
                Showing {from.toLocaleString("en-IN")}–{to.toLocaleString("en-IN")} of {total.toLocaleString("en-IN")}
              </p>
              <div className="flex items-center gap-2">
                <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
                  <SelectTrigger className="h-9 w-[104px] text-xs" data-testid="assigned-page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)} data-testid={`assigned-page-size-${n}`}>{n} / page</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="h-9" disabled={loading || data.page <= 1}
                  onClick={() => goTo(data.page - 1)} data-testid="assigned-prev-page">
                  <ChevronLeft className="h-4 w-4" /> Prev
                </Button>
                <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap" data-testid="assigned-page-indicator">
                  Page {data.page} of {data.pages}
                </span>
                <Button variant="outline" size="sm" className="h-9" disabled={loading || data.page >= data.pages}
                  onClick={() => goTo(data.page + 1)} data-testid="assigned-next-page">
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
